import DOMPurify from "dompurify";
import { rewriteInternalLinks } from "@/lib/blog-utils";

interface GhostContentProps {
  html: string;
  className?: string;
}

const GhostContent = ({ html, className = "" }: GhostContentProps) => {
  const processedHtml = rewriteInternalLinks(html);
  
  // Sanitize HTML to prevent XSS attacks from external content
  const sanitizedHtml = DOMPurify.sanitize(processedHtml, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'b', 'i', 's', 'sub', 'sup',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'figure', 'figcaption',
      'div', 'span',
      'iframe',
      'hr',
      'video', 'source', 'audio'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id',
      'target', 'rel', 'width', 'height', 'style',
      'type', 'controls', 'autoplay', 'loop', 'muted', 'poster',
      'loading', 'decoding', 'srcset', 'sizes',
      'frameborder', 'allowfullscreen', 'allow'
    ],
    ADD_ATTR: ['target'],
    ALLOW_DATA_ATTR: false
  });

  return (
    <>
      <style>{`
        .ghost-content {
          line-height: 1.8;
          font-size: 1.125rem;
        }

        .ghost-content h1,
        .ghost-content h2,
        .ghost-content h3,
        .ghost-content h4,
        .ghost-content h5,
        .ghost-content h6 {
          margin-top: 2em;
          margin-bottom: 0.75em;
          font-weight: 700;
          line-height: 1.3;
        }

        .ghost-content h2 {
          font-size: 1.75rem;
        }

        .ghost-content h3 {
          font-size: 1.5rem;
        }

        .ghost-content h4 {
          font-size: 1.25rem;
        }

        .ghost-content p {
          margin-bottom: 1.5em;
        }

        .ghost-content a {
          color: hsl(var(--primary));
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .ghost-content a:hover {
          color: hsl(var(--accent));
        }

        .ghost-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.75rem;
          margin: 2em auto;
          display: block;
        }

        .ghost-content figure {
          margin: 2em 0;
        }

        .ghost-content figcaption {
          text-align: center;
          font-size: 0.875rem;
          color: hsl(var(--muted-foreground));
          margin-top: 0.75em;
        }

        .ghost-content blockquote {
          border-left: 4px solid hsl(var(--primary));
          padding-left: 1.5em;
          margin: 2em 0;
          font-style: italic;
          color: hsl(var(--muted-foreground));
        }

        .ghost-content ul,
        .ghost-content ol {
          margin: 1.5em 0;
          padding-left: 1.5em;
        }

        .ghost-content li {
          margin-bottom: 0.5em;
        }

        .ghost-content pre {
          background: hsl(var(--muted));
          border-radius: 0.5rem;
          padding: 1.5em;
          overflow-x: auto;
          margin: 2em 0;
          font-size: 0.875rem;
        }

        .ghost-content code {
          background: hsl(var(--muted));
          padding: 0.2em 0.4em;
          border-radius: 0.25rem;
          font-size: 0.875em;
        }

        .ghost-content pre code {
          background: transparent;
          padding: 0;
        }

        .ghost-content hr {
          border: none;
          border-top: 1px solid hsl(var(--border));
          margin: 3em 0;
        }

        .ghost-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 2em 0;
        }

        .ghost-content th,
        .ghost-content td {
          border: 1px solid hsl(var(--border));
          padding: 0.75em 1em;
          text-align: left;
        }

        .ghost-content th {
          background: hsl(var(--muted));
          font-weight: 600;
        }

        /* Ghost card styles */
        .ghost-content .kg-card {
          margin: 2em 0;
        }

        .ghost-content .kg-image-card img {
          margin: 0 auto;
        }

        .ghost-content .kg-gallery-card {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1em;
        }

        .ghost-content .kg-gallery-image img {
          margin: 0;
        }

        .ghost-content .kg-bookmark-card {
          border: 1px solid hsl(var(--border));
          border-radius: 0.75rem;
          overflow: hidden;
        }

        .ghost-content .kg-bookmark-container {
          display: flex;
          text-decoration: none;
          color: inherit;
        }

        .ghost-content .kg-bookmark-content {
          padding: 1.25em;
          flex: 1;
        }

        .ghost-content .kg-bookmark-title {
          font-weight: 600;
          margin-bottom: 0.5em;
        }

        .ghost-content .kg-bookmark-description {
          font-size: 0.875rem;
          color: hsl(var(--muted-foreground));
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .ghost-content .kg-bookmark-metadata {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5em;
          margin-top: 0.75em;
          font-size: 0.875rem;
          color: hsl(var(--muted-foreground));
        }

        .ghost-content .kg-bookmark-metadata > * {
          display: inline-flex;
          align-items: center;
        }

        .ghost-content .kg-bookmark-icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          margin-right: 0.25em;
        }

        .ghost-content .kg-bookmark-author {
          font-weight: 500;
        }

        .ghost-content .kg-bookmark-author::after {
          content: "•";
          margin: 0 0.5em;
          font-weight: 400;
        }

        .ghost-content .kg-bookmark-publisher {
          font-weight: 400;
        }

        .ghost-content .kg-bookmark-thumbnail {
          width: 160px;
          min-height: 100%;
        }

        .ghost-content .kg-bookmark-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          margin: 0;
          border-radius: 0;
        }

        .ghost-content .kg-callout-card {
          padding: 1.25em;
          border-radius: 0.75rem;
          background: hsl(var(--muted));
          display: flex;
          gap: 0.75em;
        }

        .ghost-content .kg-callout-emoji {
          font-size: 1.25em;
        }

        .ghost-content .kg-video-card video {
          width: 100%;
          border-radius: 0.75rem;
        }

        .ghost-content .kg-embed-card iframe {
          width: 100%;
          border-radius: 0.75rem;
        }

        @media (max-width: 640px) {
          .ghost-content {
            font-size: 1rem;
          }

          .ghost-content h2 {
            font-size: 1.5rem;
          }

          .ghost-content h3 {
            font-size: 1.25rem;
          }

          .ghost-content .kg-bookmark-container {
            flex-direction: column;
          }

          .ghost-content .kg-bookmark-thumbnail {
            width: 100%;
            height: 200px;
          }
        }
      `}</style>
      <div
        className={`ghost-content ${className}`}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </>
  );
};

export default GhostContent;
